import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { Order } from '../types';
import { ordersAPI, settingsAPI } from '../services/api';
import { useCurrency } from '../contexts/CurrencyContext';
import toast from 'react-hot-toast';
import { printStandardReceipt } from '../utils/printReceipt';

const Receipt: React.FC = () => {
  const { orderId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  
  const [order, setOrder] = useState<Order | null>(null);
  const [loading, setLoading] = useState(true);
  const [receiptSettings, setReceiptSettings] = useState({
    receiptTitle: '',
    receiptSubtitle: '',
    receiptPhone: '',
    receiptTaxNumber: '',
    receiptFooter: '',
    receiptDateLabel: '',
    receiptTimeLabel: '',
    receiptOrderNumberLabel: '',
    receiptOrderDetailsLabel: '',
    receiptPaymentLabel: '',
    receiptPaymentCash: '',
    receiptPaymentCard: '',
    receiptSubtotalLabel: '',
    receiptTaxLabel: '',
    receiptTotalLabel: '',
    receiptQuantityLabel: '',
    receiptShowTaxDetails: false,
    receiptShowPaymentType: false,
    receiptShowOrderNumber: false,
    receiptShowDateTime: false
  });
  
  const { formatPrice } = useCurrency();
  
  // Payment sayfasından gelen veriler
  const { paymentType, completedAt } = location.state || {};
  
  // Ödeme tarihi hesaplama
  const paymentDate = completedAt ? new Date(completedAt) : new Date(order?.paid_at || order?.created_at || Date.now());

  useEffect(() => {
    loadOrder();
    loadReceiptSettings();
  }, [orderId]);

  const loadOrder = async () => {
    if (!orderId) {
      navigate('/');
      return;
    }

    try {
      const orderData = await ordersAPI.getById(parseInt(orderId));
      setOrder(orderData);
    } catch (error) {
      toast.error('Sipariş bulunamadı');
      navigate('/');
    } finally {
      setLoading(false);
    }
  };

  const loadReceiptSettings = async () => {
    try {
      const settings = await settingsAPI.getReceiptSettings();
      setReceiptSettings({
        receiptTitle: settings.receiptTitle || 'GoldCafeT OrderSystem',
        receiptSubtitle: settings.receiptSubtitle || '',
        receiptPhone: settings.receiptPhone || '',
        receiptTaxNumber: settings.receiptTaxNumber || '',
        receiptFooter: settings.receiptFooter || 'Bizi tercih ettiğiniz için teşekkür ederiz! ☕',
        receiptDateLabel: settings.receiptDateLabel || 'Tarih:',
        receiptTimeLabel: settings.receiptTimeLabel || 'Saat:',
        receiptOrderNumberLabel: settings.receiptOrderNumberLabel || 'Sipariş No:',
        receiptOrderDetailsLabel: settings.receiptOrderDetailsLabel || 'SİPARİŞ DETAYI',
        receiptPaymentLabel: settings.receiptPaymentLabel || 'Ödeme Türü:',
        receiptPaymentCash: settings.receiptPaymentCash || 'Nakit',
        receiptPaymentCard: settings.receiptPaymentCard || 'Kart',
        receiptSubtotalLabel: settings.receiptSubtotalLabel || 'Ara Toplam:',
        receiptTaxLabel: settings.receiptTaxLabel || 'KDV:',
        receiptTotalLabel: settings.receiptTotalLabel || 'TOPLAM:',
        receiptQuantityLabel: settings.receiptQuantityLabel || 'x',
        receiptShowTaxDetails: settings.receiptShowTaxDetails !== false,
        receiptShowPaymentType: settings.receiptShowPaymentType !== false,
        receiptShowOrderNumber: settings.receiptShowOrderNumber !== false,
        receiptShowDateTime: settings.receiptShowDateTime !== false
      });
    } catch (error) {
      // console.error('Fiş ayarları yüklenemedi:', error);
    }
  };

  const printReceipt = () => {
    if (!order) return;
    
    // Standart fiş yazdırma fonksiyonunu kullan
    printStandardReceipt(order, receiptSettings, formatPrice);
  };

  const finishAndReturn = () => {
    navigate('/');
    toast.success('İşlem tamamlandı');
  };

  // Sayfa yüklendiğinde otomatik olarak standart fiş formatını aç
  useEffect(() => {
    if (order && !loading) {
      printReceipt();
    }
  }, [order, loading]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-cafe-cream">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-cafe-brown"></div>
        <span className="ml-2 text-cafe-brown">Yükleniyor...</span>
      </div>
    );
  }

  if (!order) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-cafe-cream">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-800 mb-4">Sipariş bulunamadı</h2>
          <button
            onClick={() => navigate('/')}
            className="bg-cafe-brown text-white px-6 py-2 rounded-lg hover:bg-cafe-dark transition-colors"
          >
            Ana Sayfaya Dön
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-cafe-cream dark:bg-dark-bg flex items-center justify-center">
      <div className="text-center">
        <div className="bg-white dark:bg-dark-surface rounded-lg shadow-lg p-8 max-w-md mx-auto">
          <div className="text-green-600 mb-4">
            <div className="text-5xl mb-3">✅</div>
            <h2 className="text-2xl font-bold mb-2">Ödeme Tamamlandı!</h2>
            <p className="text-gray-600 dark:text-dark-text mb-4">
              Sipariş #{order?.id}<br />
              {!order?.table ? 'Hızlı Sipariş' : `Masa ${order.table.table_number}`}<br />
              Toplam: {formatPrice(order?.total_amount || 0)}<br />
              Ödeme Türü: {(paymentType || order?.payment_type || 'N/A').toUpperCase()}
            </p>
          </div>
          
          <div className="space-y-3">
            <button
              onClick={printReceipt}
              className="w-full bg-cafe-brown dark:bg-cafe-dark text-white px-6 py-3 rounded-lg hover:bg-cafe-dark dark:hover:bg-cafe-brown transition-colors font-medium"
            >
              🖨️ Fiş Tekrar Yazdır
            </button>
            <button
              onClick={finishAndReturn}
              className="w-full bg-green-600 text-white px-6 py-3 rounded-lg hover:bg-green-700 transition-colors font-medium"
            >
              ✅ Ana Sayfaya Dön
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Receipt;
